import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { ENDPOINTS } from "../Services/ServiceConfig";

// Feature flag: Enable static file fetching (set to true when static files are deployed)
// When false, goes directly to API (Redis → Kusto) without 404 latency from missing files
const ENABLE_STATIC_FILE_FETCH = false;

// Get API root URL (remove /api/ suffix for static files)
const getStaticFileBaseUrl = () => {
  const apiUrl = ENDPOINTS.BASE_URL || "";
  return apiUrl.replace(/\/api\/?$/, "");
};

/**
 * Good State Health API (Baseline Security Health Tab).
 *
 * Data Flow:
 * - ENABLE_STATIC_FILE_FETCH=true: Static JSON file first (~50ms), then API fallback
 * - ENABLE_STATIC_FILE_FETCH=false: API directly (Redis → Kusto, ~50ms with prewarm)
 *
 * Admin generates static files via POST /api/admin/staticData/baselineSecurityHealth/generate
 */
export interface BaselineSecurityHealthTenantMetrics {
  tenantId: string;
  tenantName: string;
  /**
   * Top-parent (TPID-level) customer name, e.g. "Contoso Corp". Sourced from
   * Kusto column `CustomerName`. Returns empty string when the upstream
   * function hasn't projected the column yet.
   */
  customerName: string;
  /** "S500" | "Non-S500" */
  s500Status: string;
  /** Granular segment name (e.g. "Strategic Commercial"). */
  segmentName: string;
  /** Industry classification (e.g. "Retail", "Financial Services"). */
  industry: string;
  /** Total number of active KPIs. */
  overallActiveKPI: number;
  /** Number of KPIs meeting standard. */
  overallMetStandard: number;
  /** Overall risk status: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  overallRiskStatus: string;
  /** Baseline initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  baselineRiskStatus: string;
  /** Passkey initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  passkeyRiskStatus: string;
  /** Patching initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  patchingRiskStatus: string;
  /** Endpoint initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  endpointRiskStatus: string;
  /** Protection initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  protectionRiskStatus: string;
  /** Hygiene initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  hygieneRiskStatus: string;
  /** Dormant account initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  dormantAccountRiskStatus: string;
  /** Vulnerabilities initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  vulnerabilitiesRiskStatus: string;
  /** Monitoring initiative adoption band: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  monitoringRiskStatus: string;
  /** Overall score percentage (0-100). */
  overallScorePercent: number;
  vertical: string;
  /** Coarse segment (e.g. "Enterprise", "SME&C Corporate", "SME&C SMB", "EDU"). */
  segment: string;
  region: string;
  entraIdLogins: number;
  /** License SKU (e.g. "M365 E3", "M365 E5"). */
  licenseSKU: string;
  topParentId: string;
  /** Engagement cohort (e.g. "FastTrack Managed"). */
  engagementCohort: string;
  /** Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  mom: string;
  /** Baseline initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  baselineMOM: string;
  /** Passkey initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  passkeyMOM: string;
  /** Patching initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  patchingMOM: string;
  /** Endpoint initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  endpointMOM: string;
  /** Protection initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  protectionMOM: string;
  /** Hygiene initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  hygieneMOM: string;
  /** Dormant account initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  dormantAccountMOM: string;
  /** Vulnerabilities initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  vulnerabilitiesMOM: string;
  /** Monitoring initiative Month-over-Month change (signed value as a string, e.g. "+14", "+0", "-3"). */
  monitoringMOM: string;
  /** Start of the Month-over-Month comparison window (ISO-8601 datetime string). */
  monthlyStartDate: string | null;
  /** End of the Month-over-Month comparison window (ISO-8601 datetime string). */
  monthlyEndDate: string | null;
  /** Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  wow: string;
  /** Baseline initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  baselineWOW: string;
  /** Passkey initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  passkeyWOW: string;
  /** Patching initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  patchingWOW: string;
  /** Endpoint initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  endpointWOW: string;
  /** Protection initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  protectionWOW: string;
  /** Hygiene initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  hygieneWOW: string;
  /** Dormant account initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  dormantAccountWOW: string;
  /** Vulnerabilities initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  vulnerabilitiesWOW: string;
  /** Monitoring initiative Week-over-Week change (signed value as a string, e.g. "+14", "+0", "-3"). */
  monitoringWOW: string;
  /** Start of the Week-over-Week comparison window (ISO-8601 datetime string). */
  weeklyStartDate: string | null;
  /** End of the Week-over-Week comparison window (ISO-8601 datetime string). */
  weeklyEndDate: string | null;
  /** ISO-8601 datetime string from JSON serialization. */
  snapshotDate: string | null;
}

export interface BaselineSecurityHealthTenantMetricsResponse {
  tenants: BaselineSecurityHealthTenantMetrics[];
  kustoLink?: string | null;
}

export interface BaselineSecurityHealthRiskRollup {
  totalTenants: number;
  highRiskCount: number;
  moderateRiskCount: number;
  lowRiskCount: number;
  highRiskPercentage: number;
  moderateRiskPercentage: number;
  lowRiskPercentage: number;
  /** Average actions per tenant (percentage based on OverallMetStandard). */
  avgActionsPerTenant: number;
  /** Total number of actions across all tenants. */
  totalActions: number;
}

/**
 * One metric row within a Good State Health category section.
 */
export interface GoodStateHealthMetric {
  /** Stable numeric KPI id from Kusto (`KPIId`). */
  kpiId?: string;
  /** Human-readable metric name. */
  metricName: string;
  metricDescription: string;
  /** Display-ready benchmark string (e.g. "100%", "> 40% sensor", "EXISTS"). */
  benchmark: string;
  /** The tenant's measured value for the metric (display-ready string). */
  tenantValue: string;
  /** "Meets standard" | "Low Adoption" | "Pending Partner Input" (case-insensitive). */
  status: string;
  /** Status colour keyword from Kusto ("Green" | "Red" | "Grey"). Mapped to the UI palette. */
  color?: string;
  /** Tooltip copy for the recommendation card (may be empty). */
  tooltip?: string;
  /** Display order for the recommendation card (ascending). */
  orderMetric?: number;
  /** Directly Responsible Individual name(s). Comma-separated when more than one. */
  dri?: string;
  /** DRI email(s), aligned by position with `dri`. Comma-separated when more than one. */
  driEmail?: string;
  /** FastTrack flag/tag per metric (e.g. "FastTrack Targeting"). Empty when not targeted. */
  fastTrackTargetTag?: string;
}

/**
 * One category section within the Good State Health detail.
 */
export interface GoodStateHealthCategory {
  /** Stable category key derived from the API column prefix (for example: "Baseline", "Passkey", "Protection"). */
  categoryKey: string;
  /** Category display name (e.g. "Baseline: Defend against the most common breach vectors"). */
  categoryName: string;
  /** Total number of active KPIs in this category. */
  totalActiveKPI: number;
  /** Number of KPIs meeting standard in this category. */
  totalMetStandard: number;
  /** Score percentage (0-100) for this category. */
  scorePercent: number;
  /** Risk status: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  riskStatus: string;
  /** Per-metric breakdown for this category. */
  details: GoodStateHealthMetric[];
}

export interface BaselineSecurityHealthCustomerDetail {
  // Header
  snapshotDate: string | null;
  tenantId: string;
  tenantName: string;
  topParentId: string;
  /** "S500" | "Non-S500" */
  s500Status: string;
  /** Raw license SKU (e.g. "M365 E5"). */
  licenseSKU: string;
  e3IncludedQty: number;
  e5IncludedQty: number;
  e7IncludedQty: number;

  // Overall Good State Health
  /** Total number of active KPIs across all categories. */
  overallActiveKPI: number;
  /** Total number of KPIs meeting standard across all categories. */
  overallMetStandard: number;
  /** Overall score percentage (0-100). */
  overallScorePercent: number;
  /** Overall risk status: "High Adoption" | "Moderate Adoption" | "Low Adoption". */
  overallRiskStatus: string;

  /** All category sections returned by the upstream function, in API order. */
  categories: GoodStateHealthCategory[];

  // Category Sections
  /** Baseline: Defend against the most common breach vectors. */
  baseline: GoodStateHealthCategory;
  /** Phishing-resistant Authentication (Passkey). */
  passkey: GoodStateHealthCategory;
  /** Patching: Stay on current software. */
  patching: GoodStateHealthCategory;
  /** Find and fix vulnerabilities in your codebase. */
  vulnerabilities: GoodStateHealthCategory;
}

/**
 * Aggregated M365 license counts for a single tenant.
 *
 * Computed server-side from `Fn_MultiTenantSubscriptionDetails` using:
 *  - E3: `SkuAssigned == "M365"` AND `SkuCategory == "E3"`
 *  - E5: `SkuAssigned == "M365"` AND `SkuCategory in ("E5", "E5 S&C")`
 *  - E7: `SkuAssigned == "M365"` AND `SkuCategory == "E7"`
 *
 * Each value sums `IncludedQuantity` across matching subscription rows.
 * The endpoint always returns exactly one record (zeros if nothing matched).
 */
export interface BaselineSecurityHealthTenantLicenses {
  tenantId: string;
  e3IncludedQty: number;
  e5IncludedQty: number;
  e7IncludedQty: number;
}

/**
 * Server-side filter for the Ready for AI adoption trend chart and card metrics.
 * Mirrors the 15 string parameters exposed by the Kusto functions. Every field
 * defaults to "AllUp" (no filter). Adoption bands accept a single value
 * (e.g. "Low Adoption") or a comma-joined list ("Low Adoption,Moderate Adoption").
 */
export interface AIReadyHealthFilter {
  overallRiskStatus: string;
  baselineRiskStatus: string;
  passkeyRiskStatus: string;
  patchingRiskStatus: string;
  endpointRiskStatus: string;
  protectionRiskStatus: string;
  hygieneRiskStatus: string;
  dormantAccountRiskStatus: string;
  vulnerabilitiesRiskStatus: string;
  monitoringRiskStatus: string;
  s500Status: string;
  vertical: string;
  region: string;
  licenseSKU: string;
  engagementCohort: string;
}

/** One point on the adoption trend chart (tenant counts per band at a snapshot). */
export interface AIReadyHealthTrendPoint {
  /** Weekly snapshot date (ISO-8601 datetime string). */
  factDate: string | null;
  /** Initiative this row belongs to (e.g. "Overall", "Passkey"). */
  initiative: string;
  /** Tenants in the Low Adoption band. */
  low: number;
  /** Tenants in the Moderate Adoption band. */
  moderate: number;
  /** Tenants in the High Adoption band. */
  high: number;
}

export interface AIReadyHealthTrendResponse {
  points: AIReadyHealthTrendPoint[];
  kustoLink?: string | null;
}

/** Filtered KPI card metrics for the Ready for AI landing view (single row). */
export interface AIReadyHealthCardMetrics {
  currDate: string | null;
  prevDate: string | null;
  prevPrevDate: string | null;
  cumulativeStartDate: string | null;
  totalTenants: number;
  low: number;
  lowWoW: number;
  moderate: number;
  moderateWoW: number;
  high: number;
  highWoW: number;
  totalControlsAdopted: number;
  totalControls: number;
  totalControlsAdoptedWoW: number | null;
  totalControlsAdoptedWoWPercentage: number | null;
  avgControlsPerTenant: number;
  avgControlsWoW: number;
  cumulativeImprovedTenants: number | null;
  tenantsImprovedThisWeek: number | null;
  tenantsImprovedPreviousWeek: number | null;
  tenantsImprovedWoW: number | null;
  tenantsImprovedWoWPercentage: number | null;
  tenantsImprovedAdoption: number;
}

export const baselineSecurityHealthAPI = createApi({
  reducerPath: "baselineSecurityHealth",
  baseQuery: baseQueryWithReauth,
  // Cache settings for optimal performance
  keepUnusedDataFor: 86400, // 24 hours - static data
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    /**
     * Fetches all tenant metrics for the Baseline Security Health grid.
     * Tries static JSON file first (~50ms), falls back to API → Redis → Kusto.
     */
    getBaselineSecurityHealthTenantMetrics: builder.query<BaselineSecurityHealthTenantMetricsResponse, void>({
      queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
        // Try static file first if enabled (avoids 404 latency when files don't exist)
        if (ENABLE_STATIC_FILE_FETCH) {
          try {
            const staticUrl = `${getStaticFileBaseUrl()}/data/baselineSecurityHealth/tenant-metrics.json`;
            const staticResponse = await fetch(staticUrl);
            if (staticResponse.ok) {
              const staticData = await staticResponse.json() as BaselineSecurityHealthTenantMetricsResponse;
              return { data: staticData };
            }
          } catch {
            // Static file doesn't exist, fall through to API
          }
        }

        // API (Redis → Kusto) - prewarm service keeps Redis hot
        const result = await baseQuery({
          url: "baselineSecurityHealth/getTenantMetrics",
          method: "GET"
        });
        
        if (result.error) {
          return { error: result.error };
        }
        
        return { data: result.data as BaselineSecurityHealthTenantMetricsResponse };
      },
      keepUnusedDataFor: 86400
    }),
    /**
     * Fetches risk rollup KPIs (High/Moderate/Low counts).
     * Tries static JSON file first, falls back to API.
     */
    getBaselineSecurityHealthRiskRollup: builder.query<BaselineSecurityHealthRiskRollup, void>({
      queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
        // Try static file first if enabled
        if (ENABLE_STATIC_FILE_FETCH) {
          try {
            const staticUrl = `${getStaticFileBaseUrl()}/data/baselineSecurityHealth/risk-rollup.json`;
            const staticResponse = await fetch(staticUrl);
            if (staticResponse.ok) {
              const staticData = await staticResponse.json() as BaselineSecurityHealthRiskRollup;
              return { data: staticData };
            }
          } catch {
            // Static file doesn't exist, fall through to API
          }
        }

        // API (Redis → Kusto) - prewarm service keeps Redis hot
        const result = await baseQuery({
          url: "baselineSecurityHealth/getRiskRollup",
          method: "GET"
        });
        
        if (result.error) {
          return { error: result.error };
        }
        
        return { data: result.data as BaselineSecurityHealthRiskRollup };
      },
      keepUnusedDataFor: 86400
    }),
    getBaselineSecurityHealthCustomerDetail: builder.query<BaselineSecurityHealthCustomerDetail | null, string>({
      query: (tenantId: string) => `baselineSecurityHealth/getCustomerDetail?tenantId=${encodeURIComponent(tenantId)}`,
      keepUnusedDataFor: 300
    }),
    getBaselineSecurityHealthTenantLicenses: builder.query<BaselineSecurityHealthTenantLicenses, string>({
      query: (tenantId: string) => `baselineSecurityHealth/getTenantLicenses?tenantId=${encodeURIComponent(tenantId)}`,
      keepUnusedDataFor: 300
    }),
    /**
     * Fetches the filtered Ready for AI adoption trend (tenant counts per band
     * over time). Server-side filtered via the supplied criteria.
     */
    getAIReadyHealthTrend: builder.query<AIReadyHealthTrendResponse, AIReadyHealthFilter>({
      query: (filter: AIReadyHealthFilter) => ({
        url: "baselineSecurityHealth/getAdoptionTrend",
        method: "POST",
        body: filter
      }),
      keepUnusedDataFor: 300
    }),
    /**
     * Fetches the filtered Ready for AI KPI card metrics. Server-side filtered
     * via the supplied criteria.
     */
    getAIReadyHealthCardMetrics: builder.query<AIReadyHealthCardMetrics, AIReadyHealthFilter>({
      query: (filter: AIReadyHealthFilter) => ({
        url: "baselineSecurityHealth/getCardMetrics",
        method: "POST",
        body: filter
      }),
      keepUnusedDataFor: 300
    })
  })
});

export const {
  useGetBaselineSecurityHealthTenantMetricsQuery,
  useGetBaselineSecurityHealthRiskRollupQuery,
  useGetBaselineSecurityHealthCustomerDetailQuery,
  useGetBaselineSecurityHealthTenantLicensesQuery,
  useGetAIReadyHealthTrendQuery,
  useGetAIReadyHealthCardMetricsQuery
} = baselineSecurityHealthAPI;
